import React, { useState, useEffect, useMemo } from 'react';
import { 
  ShieldAlert, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  RefreshCw, 
  Server, 
  TrendingUp, 
  ArrowRight, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  Filter, 
  Check,
  Download,
  LockKeyhole,
  Info
} from 'lucide-react';
import { Agent, SafetyEvent, Incident, DashboardStats } from '../types';
import { api } from '../services/api';
import { WorkflowGuide } from './WorkflowGuide';
import { ReviewModal } from './ReviewModal';

interface DashboardProps {
  setActiveTab: (tab: string) => void;
  onSelectAgent?: (agent: Agent) => void;
  readOnly?: boolean;
  demoMode?: boolean;
  role?: 'owner' | 'admin' | 'operator' | 'viewer';
}

interface IncidentGroup {
  key: string;
  incidents: Incident[];
  latest: Incident;
  activeCount: number;
  firstSeen: string;
  lastSeen: string;
}

const EMPTY_STATS: DashboardStats = {
  fleet: { total_agents: 0, protected: 0, monitoring: 0, at_risk: 0, quarantined: 0 },
  telemetry: { total_events: 0, allowed: 0, reviewed: 0, blocked: 0, interventions_rate: 0 },
  incidents: { open_count: 0 },
  organizational_patterns: []
};

export const CentralDashboard: React.FC<DashboardProps> = ({ setActiveTab, readOnly = false, demoMode = false, role = 'admin' }) => {
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [filterDecision, setFilterDecision] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchEvents, setSearchEvents] = useState<string>('');
  const [filterAgentId, setFilterAgentId] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [incidentNotes, setIncidentNotes] = useState<Record<string, string>>({});
  const [isInventoryCollapsed, setIsInventoryCollapsed] = useState<boolean>(false);
  const [isEventsCollapsed, setIsEventsCollapsed] = useState<boolean>(false);
  const [isIncidentsCollapsed, setIsIncidentsCollapsed] = useState<boolean>(false);
  const [reviewModalIncident, setReviewModalIncident] = useState<Incident | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, agentsData, eventsData, incidentsData] = await Promise.all([
        api.getDashboardStats(),
        api.getAgents(),
        api.getEvents({ decision: filterDecision || undefined, agent_id: filterAgentId || undefined, limit: 100 }),
        api.getIncidents()
      ]);
      setStats(statsData);
      setAgents(agentsData);
      setEvents(eventsData);
      setIncidents(incidentsData);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Dashboard data is unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [filterDecision, filterAgentId]);

  const handleIncidentAction = async (id: string, action: 'acknowledge' | 'approve' | 'reject' | 'resolve') => {
    if (action === 'approve' && !window.confirm('Approve and close this held action? This records authorization; it does not replay the action.')) return;
    if (action === 'reject' && !window.confirm('Reject and close this held action? The action will remain stopped.')) return;
    setResolvingId(id);
    setActionError(null);
    try {
      await api.incidentAction(id, action, incidentNotes[id]);
      await loadData();
    } catch (err) {
      console.error('Failed to resolve incident:', err);
      setActionError(err instanceof Error ? err.message : 'Incident action failed.');
    } finally {
      setResolvingId(null);
    }
  };

  const handleQuarantine = async (id: string) => {
    if (readOnly || !window.confirm('Quarantine this agent? New actions from it will be stopped until an operator restores it.')) return;
    setResolvingId(id);
    setActionError(null);
    try {
      await api.quarantineIncidentAgent(id);
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to quarantine the agent.');
    } finally {
      setResolvingId(null);
    }
  };

  const handleExportEvidence = async (agentId: string) => {
    setActionError(null);
    try {
      const evidence = await api.exportEvidence({ agent_id: agentId });
      const blob = new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `agenticscale-evidence-${agentId}-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to export incident evidence.');
    }
  };

  const visibleEvents = events.filter((event) => {
    const needle = searchEvents.trim().toLowerCase();
    if (!needle) return true;
    return `${event.agent_name || event.agent_id} ${event.action_name} ${event.payload_summary}`.toLowerCase().includes(needle);
  });

  const incidentGroups = useMemo<IncidentGroup[]>(() => {
    const groups = new Map<string, IncidentGroup>();
    incidents
      .filter((incident) => incident.status !== 'resolved')
      .forEach((incident) => {
        const action = incident.action_name || incident.title;
        const key = `${incident.agent_id}:${action}:${incident.decision || 'INCIDENT'}`;
        const timestamp = incident.event_time || incident.created_at;
        const existing = groups.get(key);
        if (existing) {
          existing.incidents.push(incident);
          existing.activeCount += 1;
          if (new Date(timestamp).getTime() < new Date(existing.firstSeen).getTime()) existing.firstSeen = timestamp;
          if (new Date(timestamp).getTime() > new Date(existing.lastSeen).getTime()) {
            existing.lastSeen = timestamp;
            existing.latest = incident;
          }
        } else {
          groups.set(key, {
            key,
            incidents: [incident],
            latest: incident,
            activeCount: 1,
            firstSeen: timestamp,
            lastSeen: timestamp
          });
        }
      });

    return Array.from(groups.values()).sort((left, right) => new Date(right.lastSeen).getTime() - new Date(left.lastSeen).getTime());
  }, [incidents]);

  const getDecisionBadge = (decision: string) => {
    switch (decision) {
      case 'ALLOW':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30 shrink-0">
            <CheckCircle className="w-3 h-3 mr-1" /> ALLOW
          </span>
        );
      case 'REVIEW':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30 shrink-0">
            <AlertTriangle className="w-3 h-3 mr-1" /> REVIEW REQUIRED
          </span>
        );
      case 'BLOCK':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400 border border-rose-300 dark:border-rose-500/30 shrink-0">
            <XCircle className="w-3 h-3 mr-1" /> BLOCKED
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'protected':
        return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">Protected</span>;
      case 'monitoring':
        return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">Monitoring</span>;
      case 'at_risk':
        return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">At-Risk</span>;
      case 'quarantined':
        return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30">Quarantined</span>;
      default:
        return null;
    }
  };

  const getRiskTierBadge = (agent: Agent) => {
    const isHigh = agent.risk_score > 60 || agent.blast_radius === 'critical' || agent.status === 'at_risk';
    const isMed = agent.risk_score > 30 || agent.blast_radius === 'high' || agent.blast_radius === 'medium';
    
    if (isHigh) {
      return (
        <span className="inline-flex min-w-[78px] justify-center items-center px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300 border border-rose-300 dark:border-rose-500/30">
          High Risk
        </span>
      );
    } else if (isMed) {
      return (
        <span className="inline-flex min-w-[78px] justify-center items-center px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30">
          Medium Risk
        </span>
      );
    } else {
      return (
        <span className="inline-flex min-w-[78px] justify-center items-center px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30">
          Low Risk
        </span>
      );
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      
      {/* Hero Banner */}
      <div className="glass-panel p-6 sm:p-8 rounded-2xl relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 relative z-10">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              AI Agent Safety Operations
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1.5 max-w-2xl">
              Monitor agent actions, enforce safety rules in real time, and investigate incidents across your fleet.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setActiveTab('review')}
              className="px-4 py-2.5 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-md shadow-brand-500/20 transition-all flex items-center space-x-2"
            >
              <span>Start risk review</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setActiveTab('simulate')}
              className="px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition-all flex items-center space-x-2"
            >
              <span>Launch Simulator</span>
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
              title="Refresh telemetry"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {readOnly && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60 p-3.5 text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <Info className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
          <span>
            {role === 'viewer' 
              ? '👁️ Read-Only Viewer: You can inspect fleet telemetry and open incidents. Quarantining agents and resolving incidents are disabled in this view.' 
              : 'Read-only workspace: your role can explore organization data but cannot change profiles, policies, or incidents.'}
          </span>
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-950/20 dark:text-rose-300">
          Dashboard data could not be loaded: {error}. Use Refresh to retry.
        </div>
      )}

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        
        <div className="glass-card min-w-0 p-5 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Total Agents</span>
            <Server className="w-4 h-4 text-brand-600 dark:text-brand-400" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              {stats.fleet.total_agents}
            </span>
            <span className="text-sm text-emerald-600 dark:text-emerald-400 font-semibold">Active</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{stats.fleet.protected} Protected</span>
            <span className="text-blue-600 dark:text-blue-400 font-semibold">{stats.fleet.monitoring} Monitoring</span>
            <span className="text-amber-600 dark:text-amber-400 font-semibold">{stats.fleet.at_risk} At risk</span>
            <span className="text-rose-600 dark:text-rose-400 font-semibold">{stats.fleet.quarantined} Quarantined</span>
          </div>
        </div>

        <div className="glass-card min-w-0 p-5 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Safety Interventions</span>
            <TrendingUp className="w-4 h-4 text-amber-500 dark:text-amber-400" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              {stats.telemetry.interventions_rate}%
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">Held or blocked</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="text-amber-600 dark:text-amber-400 font-semibold">{stats.telemetry.reviewed} Review</span>
            <span className="text-rose-600 dark:text-rose-400 font-semibold">{stats.telemetry.blocked} Blocked</span>
          </div>
        </div>

        <div className="glass-card min-w-0 p-5 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Open Incidents</span>
            <ShieldAlert className="w-4 h-4 text-rose-500 dark:text-rose-400" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-extrabold text-rose-600 dark:text-rose-400">
              {stats.incidents.open_count}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Active</span>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            <span>Needs attention</span>
          </div>
        </div>

      </div>

      <WorkflowGuide setActiveTab={setActiveTab} />

      {/* Main Two Columns: Fleet Inventory (6 cols) + Live Safety Events (6 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Fleet Inventory (6 cols) */}
        <div className="lg:col-span-6 min-w-0">
          <div className="glass-panel p-5 sm:p-6 rounded-2xl space-y-4">
            <div className="flex items-center justify-between gap-3 pb-1 border-b border-slate-100 dark:border-slate-800/80">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>AI Agent Inventory</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700">
                    {agents.length}
                  </span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Governance status, owners, and operating boundaries</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setActiveTab('profiles')}
                  className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-semibold flex items-center space-x-1 mr-1"
                >
                  <span>View All</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsInventoryCollapsed(!isInventoryCollapsed)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  aria-expanded={!isInventoryCollapsed}
                  title={isInventoryCollapsed ? 'Expand Inventory' : 'Collapse Inventory'}
                >
                  {isInventoryCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {!isInventoryCollapsed && (
              <div className="space-y-3 pt-1 animate-fade-in">
                {loading && agents.length === 0 ? [1, 2, 3].map((item) => <div key={item} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-900 animate-pulse" />) : agents.map((agent) => (
                  <div 
                    key={agent.id}
                    className="glass-card p-4 rounded-xl hover:border-brand-300 dark:hover:border-slate-700 transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-bold text-sm text-slate-900 dark:text-white">{agent.name}</span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                          {agent.version}
                        </span>
                        {getStatusBadge(agent.status)}
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">{agent.purpose}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                        <span>Owner: {agent.owner}</span>
                        <span>Limit: ${(agent.max_transaction_limit || 0).toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center shrink-0">
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Risk tier</div>
                        {getRiskTierBadge(agent)}
                      </div>
                      <button
                        onClick={() => setActiveTab('simulate')}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-50 hover:bg-brand-100 text-brand-700 dark:bg-brand-600/20 dark:hover:bg-brand-600/40 dark:text-brand-300 border border-brand-200 dark:border-brand-500/30 transition-all"
                      >
                        Simulate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Live Safety Events (6 cols) */}
        <div className="lg:col-span-6 min-w-0">
          <div className="glass-panel p-5 sm:p-6 rounded-2xl space-y-4">
            
            {/* Header */}
            <div className="flex items-center justify-between gap-3 pb-1 border-b border-slate-100 dark:border-slate-800/80">
              <div>
                <h2 className="event-feed-heading text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span className="min-w-0">{demoMode ? 'Demo Safety Events' : 'Safety Events'}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${demoMode ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1 ${demoMode ? 'bg-slate-400' : 'bg-emerald-500 animate-pulse'}`}></span>
                    {demoMode ? 'Example data' : 'Polling'}
                  </span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{demoMode ? 'Seeded gateway decisions and interventions' : 'Gateway audit feed and recent interventions'}</p>
              </div>

              <button
                type="button"
                onClick={() => setIsEventsCollapsed(!isEventsCollapsed)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition shrink-0"
                aria-expanded={!isEventsCollapsed}
                title={isEventsCollapsed ? 'Expand Events' : 'Collapse Events'}
              >
                {isEventsCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
            </div>

            {!isEventsCollapsed && (
              <div className="space-y-3.5 pt-1 animate-fade-in">
                {/* Filter */}
                <div className="flex flex-wrap items-center justify-end gap-2 pb-2">
                  <Filter className="w-3.5 h-3.5 text-slate-400" />
                  <label htmlFor="event-search" className="sr-only">Search events</label>
                  <input id="event-search" value={searchEvents} onChange={(event) => setSearchEvents(event.target.value)} placeholder="Search events" className="w-28 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs rounded-lg px-2 py-1 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-brand-500" />
                  <label htmlFor="event-agent-filter" className="sr-only">Filter by agent</label>
                  <select id="event-agent-filter" value={filterAgentId} onChange={(event) => setFilterAgentId(event.target.value)} className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs rounded-lg px-2 py-1 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-brand-500 shadow-sm">
                    <option value="">All Agents</option>
                    {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
                  </select>
                  <select
                    id="event-decision-filter"
                    value={filterDecision}
                    onChange={(e) => setFilterDecision(e.target.value)}
                    className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs rounded-lg px-2 py-1 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-brand-500 shadow-sm"
                  >
                    <option value="">All Decisions</option>
                    <option value="ALLOW">ALLOW</option>
                    <option value="REVIEW">REVIEW</option>
                    <option value="BLOCK">BLOCK</option>
                  </select>
                </div>

                {/* Events Cards */}
                <div className="space-y-3">
                  {loading && events.length === 0 ? <div className="space-y-3">{[1, 2, 3].map((item) => <div key={item} className="h-28 rounded-xl bg-slate-100 dark:bg-slate-900 animate-pulse" />)}</div> : visibleEvents.slice(0, 5).map((evt) => (
                    <details
                      key={evt.id}
                      className="rounded-xl bg-slate-50/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all text-xs shadow-sm"
                    >
                      <summary className="cursor-pointer list-none p-4 space-y-2.5">
                        {/* Row 1: Agent Name & Timestamp */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-sm text-slate-900 dark:text-white truncate">
                            {evt.agent_name || evt.agent_id}
                          </span>
                          <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap shrink-0">
                            {new Date(evt.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </div>

                        {/* Row 2: Action Tool Badge & Decision Tag */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 truncate max-w-[60%]">
                            {evt.action_name}()
                          </span>
                          {getDecisionBadge(evt.decision)}
                        </div>
                      </summary>

                      <div className="border-t border-slate-200 dark:border-slate-800 p-4 pt-3 space-y-2.5">
                        {/* Row 3: Action Description */}
                        <div className="text-[11px] text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-950/60 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800/60 leading-relaxed break-words">
                          {evt.payload_summary}
                        </div>

                        {/* Row 4: Policy Reasons / Violations */}
                        {Array.isArray(evt.reasons) && evt.reasons.length > 0 && (
                          <div className="space-y-1 text-[11px]">
                            {evt.reasons.map((r, i) => (
                              <div key={i} className="text-slate-600 dark:text-slate-400 flex items-start space-x-1.5">
                                <span className="text-brand-600 dark:text-brand-400 font-bold leading-none mt-0.5">•</span>
                                <span className="leading-snug">{r}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Row 5: Clean Mitigation Banner */}
                        {evt.mitigation && (
                          <div className="pt-2 border-t border-slate-200 dark:border-slate-800/80 text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                            <span className="font-semibold text-slate-800 dark:text-slate-300">Enforcement: </span>
                            <span>{evt.mitigation}</span>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                  {!loading && visibleEvents.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-xs text-slate-500">No events match the current filters.</div>}
                </div>

                {/* Bottom link to Simulator */}
                <div className="pt-1 text-center space-y-1">
                  {lastUpdated && <p className="text-[10px] text-slate-400">Last refreshed {new Date(lastUpdated).toLocaleTimeString()}</p>}
                  <button
                    onClick={() => setActiveTab('simulate')}
                    className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-semibold inline-flex items-center space-x-1"
                  >
                    <span>Test Actions in Runtime Simulator</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>

      {/* Full Width: Organization-Wide AI Safety Patterns */}
      <details className="glass-panel rounded-2xl p-6 sm:p-8">
        <summary className="cursor-pointer list-none">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Organization-wide patterns</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Recurring behavioral patterns detected across agents. Expand to inspect.</p>
        </summary>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.organizational_patterns.map((pat, idx) => (
            <div key={idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 text-xs space-y-2 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-bold text-brand-700 dark:text-brand-300 text-sm">{pat.category}</span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                  Frequency: {pat.frequency}
                </span>
              </div>
              <p className="text-slate-700 dark:text-slate-300 font-medium">⚠️ {pat.pattern}</p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold">🛡️ Safeguard: {pat.action_status}</p>
            </div>
          ))}
        </div>
      </details>

      {/* Open Incidents Drawer / Section */}
      {incidentGroups.length > 0 && (
        <div className="glass-panel p-5 sm:p-6 rounded-2xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>Incidents needing attention</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 font-bold border border-rose-200 dark:border-rose-800">
                    {incidentGroups.length} Groups
                  </span>
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400">Repeated alerts are grouped by agent, action, and decision.</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsIncidentsCollapsed(!isIncidentsCollapsed)}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-expanded={!isIncidentsCollapsed}
              title={isIncidentsCollapsed ? 'Expand Incidents' : 'Collapse Incidents'}
            >
              {isIncidentsCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>

          {!isIncidentsCollapsed && (
            <div className="space-y-3 mt-3 animate-fade-in">
            {incidentGroups.map((group) => {
              const latest = group.latest;
              const actionable = group.incidents.find((incident) => incident.status === 'open') || latest;
              const repeated = group.activeCount > 1;
              const groupLabel = repeated ? `${group.activeCount} related alerts` : '1 active alert';
              return (
                <details key={group.key} className="rounded-xl border border-rose-200 bg-white shadow-sm dark:border-rose-900/40 dark:bg-slate-900/90">
                  <summary className="cursor-pointer list-none p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="shrink-0 rounded px-2 py-0.5 font-mono font-bold text-rose-700 bg-rose-100 dark:bg-rose-500/20 dark:text-rose-300">{latest.severity}</span>
                          <span className="break-words font-bold text-slate-900 dark:text-white">{latest.title}</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-700 dark:text-slate-300">{latest.summary}</p>
                      </div>
                      <span className="shrink-0 text-[10px] text-slate-500 dark:text-slate-400">Last seen {new Date(group.lastSeen).toLocaleTimeString()}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="font-semibold text-rose-700 dark:text-rose-300">{groupLabel}</span>
                      {repeated && <span>Same agent + action; each attempt is recorded separately.</span>}
                      <span className="sm:ml-auto text-brand-700 dark:text-brand-300">Expand details</span>
                    </div>
                  </summary>

                  <div className="space-y-3 border-t border-slate-200 p-4 dark:border-slate-800">
                    <div className="rounded-lg border border-brand-100 bg-brand-50/60 px-3 py-2 text-[11px] text-brand-900 dark:border-brand-900/40 dark:bg-brand-950/20 dark:text-brand-200">
                      <span className="font-bold">Why these are grouped:</span> the same agent attempted <span className="font-mono">{latest.action_name || 'this action'}</span> and received the same {latest.decision || 'safety'} decision. Each attempt remains available in the audit trail below.
                    </div>

                    <div className="space-y-2">
                      {group.incidents.map((inc) => (
                        <div key={inc.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] dark:border-slate-800 dark:bg-slate-950/50">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{inc.status === 'acknowledged' ? 'Acknowledged' : 'Open'} · {inc.decision || 'Incident'}</span>
                            <span className="text-slate-500">{new Date(inc.event_time || inc.created_at).toLocaleString()}</span>
                          </div>
                          <p className="mt-1 text-slate-600 dark:text-slate-400">{inc.summary}</p>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-lg bg-slate-50 p-3 text-[11px] dark:bg-slate-950/80">
                      <div className="mb-1 font-semibold text-brand-700 dark:text-brand-300">Recommended response:</div>
                      {(Array.isArray(latest.runbook_steps) ? latest.runbook_steps : []).map((step, idx) => <div key={idx} className="text-slate-700 dark:text-slate-300">{step}</div>)}
                    </div>

                    {!readOnly && <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                      {actionable.status === 'open' && <button onClick={() => handleIncidentAction(actionable.id, 'acknowledge')} disabled={resolvingId === actionable.id} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">Acknowledge</button>}
                      {actionable.decision === 'REVIEW' && <>
                        <button 
                          type="button"
                          onClick={() => setReviewModalIncident(actionable)} 
                          className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-500 shadow-sm flex items-center gap-1.5"
                        >
                          <ShieldAlert className="w-3.5 h-3.5" />
                          <span>Maker-Checker Review Dialog</span>
                        </button>
                        <button onClick={() => handleIncidentAction(actionable.id, 'reject')} disabled={resolvingId === actionable.id} className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500">Reject & Close</button>
                        <button onClick={() => handleIncidentAction(actionable.id, 'approve')} disabled={resolvingId === actionable.id} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500">Approve & Close</button>
                      </>}
                      {actionable.decision !== 'REVIEW' && <button onClick={() => handleIncidentAction(actionable.id, 'resolve')} disabled={resolvingId === actionable.id} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"><Check className="h-3.5 w-3.5" />Resolve latest</button>}
                    </div>}
                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
                      <button type="button" onClick={() => handleExportEvidence(latest.agent_id)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"><Download className="h-3.5 w-3.5" />Export evidence</button>
                      {!readOnly && <button type="button" onClick={() => handleQuarantine(actionable.id)} disabled={resolvingId === actionable.id} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/30"><LockKeyhole className="h-3.5 w-3.5" />Quarantine agent</button>}
                    </div>
                    <label htmlFor={`incident-note-${actionable.id}`} className="sr-only">Decision note for {latest.title}</label>
                    {!readOnly && <textarea id={`incident-note-${actionable.id}`} value={incidentNotes[actionable.id] || ''} onChange={(event) => setIncidentNotes((previous) => ({ ...previous, [actionable.id]: event.target.value }))} rows={2} placeholder="Optional decision note for the audit trail" className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-900 dark:border-slate-800 dark:bg-slate-950/60 dark:text-white" />}
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{readOnly ? 'Demo incidents are shown for reference only. Sign in to record an operator decision.' : 'The decision note, operator, and time are saved on this incident. Blocked actions were not executed; an action that already ran must be reversed in the originating system.'}</p>
                    {actionable.decision === 'REVIEW' && <p className="text-right text-[10px] text-slate-500 dark:text-slate-400">Approval records the decision; it does not replay the held action.</p>}
                  </div>
                </details>
              );
            })}
          </div>
          )}
        </div>
      )}

      {actionError && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">{actionError}</div>}

      {/* Maker-Checker Review Modal */}
      <ReviewModal
        incident={reviewModalIncident}
        isOpen={Boolean(reviewModalIncident)}
        onClose={() => setReviewModalIncident(null)}
        onApprove={async (id, note) => {
          setIncidentNotes((prev) => ({ ...prev, [id]: note }));
          await handleIncidentAction(id, 'approve');
        }}
        onReject={async (id, note) => {
          setIncidentNotes((prev) => ({ ...prev, [id]: note }));
          await handleIncidentAction(id, 'reject');
        }}
        onQuarantine={async (id) => {
          await handleQuarantine(id);
        }}
      />

    </div>
  );
};
